<template>
  <div
    class="flex h-full flex-col space-y-4 divide-y divide-ontick-steel-500 rounded-lg border-2 border-ontick-steel-300 p-4 text-ontick-carbon-900 dark:border-none dark:bg-ontick-graphite-500 dark:text-gray-300"
  >
    <template v-if="product.plan_change_type">
      <div class="flex flex-col justify-between space-y-2">
        <h3 class="text-2xl font-bold">
          {{ product.plan_change_type.charAt(0) + product.plan_change_type.slice(1).toLowerCase() }}
          de plano
        </h3>

        <div class="flex items-center space-x-2">
          <ontick-plan-logo-component :plan="getUserPlan.plan_name" />
          <span> > </span>
          <ontick-plan-logo-component :plan="product.plan_name" />
        </div>
      </div>
      <div class="flex w-full flex-col space-y-2 pt-4">
        <dl class="plan-info flex w-full items-center justify-between">
          <dt>
            <span> Novo plano: </span>
            <span>
              {{ product.plan_name.replaceAll("_", " ") }}
            </span>
          </dt>
          <tippy :arrow="true" theme="green">
            <template v-slot:trigger>
              <dd>
                {{ product.num_cycles > 1 ? `${product.num_cycles}x` : "" }}
                {{ product.plan_price | currency }}
              </dd>
            </template>

            <ul class="text-left">
              <li>- {{ product.num_cycles }} ciclo(s)</li>
              <li>
                - {{ product.num_cycles }}x {{ $filters.currency(product.plan_price) }} =
                {{ $filters.currency(product.num_cycles * product.plan_price) }}
              </li>
            </ul>
          </tippy>
        </dl>
        <dl class="plan-info flex w-full items-center justify-between">
          <dt>Desconto:</dt>
          <dd>
            {{ ((product.discount / 100) * -1) | currency }}
          </dd>
        </dl>
        <dl class="flex w-full items-center justify-between text-base font-bold">
          <dt>Total a pagar:</dt>
          <dd class="!text-ontick-carbon-900 dark:!text-gray-300">
            {{ (product.value_to_pay / 100) | currency }}
          </dd>
        </dl>
      </div>
    </template>
    <div class="flex flex-col justify-between sm:flex-row sm:items-center" v-else>
      <div class="flex flex-col">
        <h3 class="text-2xl font-bold">
          {{
            (product.plan_name || product.name || $route.query.extension_name || $route.query.product_name).replaceAll(
              "_",
              " "
            )
          }}
        </h3>
        <p class="text-sm">
          {{
            type === "extensions" ||
            type === "product" ||
            product.new_plan_days === "month" ||
            product.period === "month"
              ? "Mensal"
              : "Anual"
          }}
        </p>
      </div>
      <p class="text-lg font-bold">
        {{
          product.plan_price || product.price || $route.query.extension_price || $route.query.product_price | currency
        }}
        <span class="font-normal text-gray-400">
          /{{
            type === "extensions" ||
            type === "product" ||
            product.new_plan_days === "month" ||
            product.period === "month"
              ? "mês"
              : "ano"
          }}
        </span>
      </p>
    </div>
    <div class="flex flex-col space-y-4 pt-4">
      <dl v-if="product.plan_change_type">
        <dt>Próximas faturas</dt>
        <dd>
          O valor total desta fatura sofreu reajuste por se tratar de um
          {{ product.plan_change_type.toLowerCase() }}. O total a pagar envolve um total de
          <span class="font-bold text-ontick-carbon-900 dark:text-gray-300">{{ product.num_cycles }}</span>
          ciclo(s) e as faturas do dia
          <span class="font-bold text-ontick-carbon-900 dark:text-gray-300">
            {{ moment(product.next_billing).format("DD/MM/YYYY") }}
          </span>
          em diante serão cobradas no valor normal de
          <span class="font-bold text-ontick-carbon-900 dark:text-gray-300"> {{ product.plan_price | currency }} </span
          >.
        </dd>
      </dl>
      <dl>
        <dt>Cancelando o pagamento automático</dt>
        <dd>
          Você poderá cancelar a cobrança automática a qualquer momento pelo menu “Assinaturas” dentro da plataforma.
        </dd>
      </dl>
      <dl>
        <dt>Segurança</dt>
        <dd>
          Nossos pagamentos são gerenciados por

          <i v-html="$icons.iugu" class="inline-flex text-[#484848] dark:text-[#fafafa]"></i>

          . Para saber mais, leia os
          <a
            href="https://www.iugu.com/juridico/termos-de-servico/"
            target="_blank"
            rel="noopener"
            class="text-ontick-blue-600 underline"
            >Termos de Serviço</a
          >
          e a
          <a
            href="https://www.iugu.com/juridico/politica-de-privacidade/"
            target="_blank"
            rel="noopener"
            class="text-ontick-blue-600 underline"
            >Política de Privacidade</a
          >
          do nosso gateway de pagamentos.
        </dd>
      </dl>

      <div id="grecaptcha" class="hidden"></div>

      <ontick-button
        color="secondary"
        :disabled="!getPayerFieldsAreValid || invalidCep === null || invalidCep === true"
        @click.native="goToPayment"
      >
        <i v-html="$icons.loading" v-if="invoiceIsLoading"></i>
        <div class="flex items-center justify-center space-x-2" v-else>
          <i v-html="$icons.open_in_new"></i>
          <span class="font-bold">Ir para pagamento</span>
        </div>
      </ontick-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ontickPlanLogoComponent from "../../../shared/components/basic/ontick-plan-logo.component.vue";
import EventBus from "../../../shared/utils/event_bus";
import customClass from "../../../shared/utils/modal-config";
export default {
  components: { ontickPlanLogoComponent },

  props: {
    useUserData: {
      type: Boolean,
    },
    product: {
      type: Object,
    },
  },

  data() {
    return {
      type: this.$route.query.type,
      invoiceIsLoading: false,
      invalidCep: false,
      modalIsActive: false,
    };
  },

  computed: {
    ...mapGetters({
      getPayerFieldsAreValid: "payment/getPayerFieldsAreValid",
      getPayerData: "payment/getPayerData",
      getUserPlan: "config/getUserPlan",
    }),
    params() {
      const params = {
        plans: {
          invoice_type: "PLAN",
          plan_id: Number(this.$route.query.plan_id),
        },
        extensions: {
          invoice_type: "EXTENSION",
          extension_id: Number(this.$route.query.extension_id),
        },
      };
      if (this.useUserData) {
        params[this.type].payer_is_user = true;
      } else {
        const payer = this.getPayerData;
        params[this.type] = {
          ...params[this.type],
          payer_name: payer.name,
          payer_cpf_cnpj: payer.cpf?.replaceAll(".", "").replaceAll("-", ""),
          payer_cep: payer.address.cep,
          payer_address: payer.address.street,
          payer_address_number: payer.address.number,
          payer_neighborhood: payer.address.neighborhood,
          payer_city: payer.address.city,
          payer_state: payer.address.state,
        };
      }
      return params[this.type];
    },
  },

  methods: {
    ...mapActions({ setDowngradeData: "payment/setDowngradeData" }),
    goToPayment() {
      this.$recaptcha()
        .then(() => {
          const params = {
            ...this.params,
            payer_is_user: this.useUserData,
          };
          this.invoiceIsLoading = true;
          this.$api
            .post(this.$endpoint.CREATE_INVOICE, params)
            .then((res) => {
              const data = res.data;
              const statuses = {
                success: () => {
                  this.$swal({
                    title: "Redirecionando",
                    iconHtml: `<div class="spinner animate-spin">${this.$icons.spinner_lg}</div>`,
                    text: "Você será redirecionado para a nossa página de pagamentos em breve",
                    timer: 3000,
                    showConfirmButton: false,
                    showCloseButton: false,
                    allowOutsideClick: false,
                    customClass: {
                      ...customClass,
                      icon: "border-none",
                    },
                  }).then(() => {
                    window.location.href = data.secure_url;
                  });
                },
                error: (err) => {
                  this.$swal({ title: "Erro", icon: "error", text: err });
                },
              };
              statuses[data.status]();
            })
            .catch((err) => {
              console.error(err);
              this.$swal("Erro", err.data.msg, "error");
            })
            .finally(() => {
              this.invoiceIsLoading = false;
            });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.invoiceIsLoading = false;
        });
    },
  },

  watch: {
    getPayerData: {
      deep: true,
      handler(val) {
        if (val.address.cep?.length !== 9) {
          this.invalidCep = null;
        }
      },
    },
    useUserData: {
      immediate: true,
      handler(val) {
        if (val) {
          EventBus.$emit("INVALID_CEP", false);
        }
      },
    },
  },

  created() {
    EventBus.$on("INVALID_CEP", (value) => {
      this.invalidCep = value;
    });
    // console.log(this.getUserPlan);
  },
};
</script>

<style lang="scss" scoped>
dl:not(.plan-info) {
  @apply space-y-2 text-sm;
  dt {
    @apply font-bold;
  }
  dd {
    @apply text-gray-500 dark:text-gray-400;
  }
}
.plan-info {
  @apply text-xs font-normal text-gray-500 dark:text-gray-400;
}
</style>
